// hooks/useAppVersionSync.js
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { setMenuPermissions } from "../app/permissionsSlice";
import { setSoftwareSettings } from "../app/settingsSlice";
import { flattenMenuTree } from "../utils/flattenMenuTree";
import { APP_VERSION } from "../config/version";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

export const useAppVersionSync = () => {
  const dispatch = useDispatch();
  const dbCode = localStorage.getItem("dbCode");

  /* --------------------------------------------------------------
     1. Resolve a guaranteed userId (real → fallback)
     -------------------------------------------------------------- */
  let rawUserId = sessionStorage.getItem("userId");
  const [userId, setUserId] = useState(rawUserId ?? "");

  const userTypeMap = { ADMIN: "1", EMPLOYEE: "2", CUSTOMERS: "3" };

  // ---- fallback once at mount ------------------------------------------------
  if (!rawUserId) {
    const storedUserType = localStorage.getItem("userType");
    if (storedUserType && userTypeMap[storedUserType]) {
      rawUserId = userTypeMap[storedUserType];
      sessionStorage.setItem("userId", rawUserId);
      setUserId(rawUserId);
    }
  }

  // ---- keep state in sync if storage changes (other tab, login, …) ----------
  useEffect(() => {
    const check = () => {
      const current = sessionStorage.getItem("userId");
      if (current && current !== userId) {
        setUserId(current);
      } else if (!current) {
        const storedUserType = localStorage.getItem("userType");
        if (storedUserType && userTypeMap[storedUserType]) {
          const fallback = userTypeMap[storedUserType];
          sessionStorage.setItem("userId", fallback);
          setUserId(fallback);
        }
      }
    };

    check(); // initial run

    const handler = (e) => {
      if (e.key === "userId" || e.key === "userType") check();
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [userId]);

  /* --------------------------------------------------------------
     2. Version-change 
     -------------------------------------------------------------- */
  useEffect(() => {
    const storedVersion = localStorage.getItem("app_version");

    // Run **only** when version differs
    if (!storedVersion || storedVersion !== APP_VERSION) {
      //console.log("Version changed → refreshing menu & settings");

      // store the new version immediately
      localStorage.setItem("app_version", APP_VERSION);

      // need db + userId
      if (!dbCode || !userId) return;

      const refresh = async () => {
        try {
          // ---- MENU -------------------------------------------------
          const menuRes = await axios.get(
            `${apiBaseUrl}/common/getMenuTree/${dbCode}/${userId}`
          );
          if (menuRes.data.success) {
            const flat = flattenMenuTree(menuRes.data.tree);
            dispatch(setMenuPermissions(flat));
          }
        } catch (e) {
          console.error("Menu refresh error:", e);
        }

        try {
          // ---- SETTINGS ---------------------------------------------
          const setRes = await axios.get(
            `${apiBaseUrl}/common/getSettings/${dbCode}`
          );
          if (setRes.data.success) {



            //console.log(setRes.data.data);
            
            dispatch(setSoftwareSettings(setRes.data.data));
          }
        } catch (e) {
          console.error("Settings refresh error:", e);
        }
      };

      refresh();
    }
  }, [dispatch, dbCode, userId,APP_VERSION]); // re-run if any of these change
};